import { Loading } from '@/components/loading';
import { ProgressScreen } from '@/components/progress-screen';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { useAuthedApi } from '@/hooks/use-authed-api';
import { useRollbackAndReplace } from '@/hooks/use-rollback-and-replace';
import { useSSE } from '@/hooks/use-sse';
import { generateAPIUrl } from '@/utils/api-url';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { fetch as expoFetch } from 'expo/fetch';
import { hc } from 'hono/client';
import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { ApiPersonaArchiveType } from '../api/persona-archive/[...everything]+api';

const personaArchiveClient = hc<ApiPersonaArchiveType>(generateAPIUrl('/'), {
  fetch: expoFetch as unknown as typeof fetch
});

async function startRestoration(token: string, personaId: string) {
  try {
    const res = await personaArchiveClient.api['persona-archive'][':id'].$post(
      {
        param: {
          id: personaId
        }
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    return res.json();
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.log('Request aborted');
      return { valid: false, error: null } as const;
    }

    return { valid: false, error: error.toString() } as const;
  }
}

export default function PersonaArchive() {
  const { id, fromChatId } = useLocalSearchParams() as {
    id: string;
    fromChatId?: string;
  };
  const startRestorationCallback = useCallback(
    (token: string) => startRestoration(token, id),
    [id]
  );
  const { loading, data } = useAuthedApi(startRestorationCallback);
  const [restorationStatus, setRestorationStatus] = useState<
    'pending' | 'completed' | 'failed'
  >('pending');
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const rollbackAndReplace = useRollbackAndReplace();

  useSSE<{
    personaArchive: { status: 'pending' | 'completed' | 'failed' };
  }>(
    generateAPIUrl(`/api/persona-archive/${id}/status`),
    (event) => {
      const newStatus = event.personaArchive.status;
      setRestorationStatus(newStatus);

      return newStatus === 'pending';
    },
    (sseError) => {
      setError(sseError.message);
    }
  );

  return (
    <SafeAreaView
      edges={['right', 'bottom', 'left']}
      className="flex-1 web:self-center web:lg:w-[50%]"
    >
      <View>
        {loading ? (
          <View className="mt-4">
            <Loading />
          </View>
        ) : data.valid ? (
          error ? (
            <View className="gap-4 pt-4">
              <Text>Restoration failed. Please try again.</Text>
              <Text>{error}</Text>
              <Button
                variant="default"
                onPress={() => {
                  router.reload();
                }}
              >
                <Text>Refresh</Text>
              </Button>
            </View>
          ) : (
            <View>
              <ProgressScreen
                progress={restorationStatus === 'completed' ? 100 : 0}
                progressText="Your Persona took a bit of a nap, so we're waking it up for you."
                completedText="Restoration complete!"
                completed={restorationStatus === 'completed'}
                progressSteps={[
                  {
                    stepType: 'restoration',
                    completed: restorationStatus === 'completed'
                  }
                ]}
              />

              {restorationStatus === 'completed' ? (
                <Button
                  variant="default"
                  className="mt-4 px-4"
                  onPress={() => {
                    if (fromChatId) {
                      rollbackAndReplace(`/chat/${fromChatId}`);
                    } else {
                      rollbackAndReplace(`/app?personaId=${id}`);
                    }
                  }}
                >
                  <Text>Continue</Text>
                </Button>
              ) : null}
            </View>
          )
        ) : data.error === 'already_restored' ? (
          <Redirect href={`/app?personaId=${id}`} />
        ) : (
          <View className="gap-4 pt-4">
            <Text>Processing failed. Please try again.</Text>
            <Text>{data.error}</Text>
            <Button
              variant="default"
              onPress={() => {
                router.replace(`/persona-archive/${id}`);
              }}
            >
              <Text>Refresh</Text>
            </Button>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}
